import { FC, ReactNode, MouseEvent, useCallback, ElementType } from "react";
import { useRouter } from "next/navigation";
import { useTracking } from "@/lib/analyticsEvents";

export type ClickableCellProps<T extends HTMLElement = HTMLDivElement> = {
  /**
   * If true, the custom handler will not be triggered when the target of the
   * event is either a link, or a child tag of a link.
   */
  ignoreLinks?: boolean;
} & (
  | {
      href: string;
      onClick?: never;
      openInNewTab?: boolean;
    }
  | {
      href?: never;
      onClick: (e: MouseEvent<T>) => void;
      openInNewTab?: never;
    }
);

export const useClickableCell = <T extends HTMLElement = HTMLDivElement>({
  ignoreLinks,
  href,
  onClick,
  openInNewTab,
}: ClickableCellProps<T>) => {
  const router = useRouter();

  // Note that we only trigger this event if an href is provided
  const { captureEvent } = useTracking({
    eventType: "linkClicked",
    eventProps: { to: href },
  });

  // We make the entire "cell" a link. In sub-items need to be separately
  // clickable then wrap them in an `InteractionWrapper`.
  const wrappedOnClick = useCallback(
    (e: MouseEvent<T>) => {
      if (ignoreLinks && (e.target as HTMLElement).closest("a")) {
        return true;
      }

      e.preventDefault();
      e.stopPropagation();

      if (onClick) {
        onClick(e);
      } else if (e.metaKey || e.ctrlKey || openInNewTab) {
        captureEvent();
        window.open(href, "_blank");
      } else {
        captureEvent();
        router.push(href);
      }
    },
    [router, ignoreLinks, href, onClick, openInNewTab, captureEvent],
  );

  return {
    onClick: wrappedOnClick,
  };
};

/**
 * By default, clicking anywhere on the clickable cell will navigate to the target
 * href. If an element needs to be clickable without doing this it should be wrapped
 * in an InteractionWrapper.
 */
export const InteractionWrapper: FC<{
  href?: string;
  openInNewTab?: boolean;
  As?: ElementType;
  children: ReactNode;
  className?: string;
}> = ({ href, openInNewTab, As = "div", children, className }) => {
  const router = useRouter();
  const onClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (href) {
        if (openInNewTab) {
          window.open(href, "_blank");
        } else {
          router.push(href);
        }
      }
    },
    [router, href, openInNewTab],
  );
  return (
    <As data-component="InteractionWrapper" onClick={onClick} className={className}>
      {children}
    </As>
  );
};
